/** Size-optimized lodash functions */

/**
 * @returns a new object with only the specified properties
 */
export const pick = <T extends object, K extends keyof T>(obj: T, keys: K[]): Pick<T, K> =>
  keys.reduce(
    (result, key) => {
      if (key in obj) result[key] = obj[key]
      return result
    },
    {} as Pick<T, K>,
  )

/**
 * Maps object key names to another ones
 */
export const mapKeys = <K extends string, V, R extends string, O extends Record<K, V>>(
  obj: O,
  map: (key: K, value: V, index: number) => R,
) =>
  Object.fromEntries(
    Object.entries<V>(obj).map(([k, v], index) => [map(k as K, v, index), v]),
  ) as Record<R, V>

/** Maps object values to another ones */
export const mapValues = <K extends string, V, R>(
  obj: Record<K, V>,
  map: (value: V, key: K, index: number) => R,
) =>
  Object.fromEntries(
    Object.entries<V>(obj).map(([k, v], index) => [k, map(v, k as K, index)]),
  ) as Record<K, R>
/**
 * Filters object properties based on a predicate function
 */
export const filterObj = <O extends object, K extends keyof O, V extends O[keyof O]>(
  obj: O,
  pred: (value: V, key: K, index: number) => boolean,
): O =>
  Object.fromEntries(
    Object.entries<V>(obj as { [key: string]: V }).filter(([k, v], index) =>
      pred(v, k as K, index),
    ),
  ) as O

export const deepMerge = <T extends Record<string, any> = Record<string, any>>(
  target: Record<string, any>,
  ...sources: Record<string, any>[]
): T => {
  if (sources.length === 0) return target as T

  const [source, ...rest] = sources

  for (const key in source)
    if (isObject(source[key]))
      deepMerge(isObject(target[key]) ? target[key] : (target[key] = {}), source[key])
    else target[key] = source[key]

  return deepMerge(target, ...rest)
}

export const isObject = (obj: any): obj is object => obj && typeof obj === "object"

export const isEmpty = (arr: unknown[]) => arr.length === 0
export const median = (arr: number[]) => {
  const sorted = arr.slice().sort((a, b) => a - b)
  const mid = Math.floor(arr.length / 2)

  return arr.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid]
}
export const includes = <T extends readonly any[]>(arr: T, item: unknown): item is T[number] => {
  for (const _item of arr) if (item === _item) return true

  return false
}
export const intersection = <T>(arr: T[], other: unknown[]): T[] => {
  const set = new Set(other)

  return arr.reduce<T[]>((acc, item) => {
    if (set.has(item)) acc.push(item)
    return acc
  }, [])
}
export const intersects = <T>(arr: T[], other: unknown[]): other is T[] =>
  intersection(arr, other).length > 0
export const isString = (value: unknown): value is string => typeof value === "string"

export const noop = () => {}
export const identity = <T>(value: T): T => value
export const throttle = <A extends unknown[], R>(
  fn: (...args: A) => R,
  wait: number,
  opts?: {
    /** Result that will be returned while throttling */
    throttledResult: (...args: A) => R
  },
): ((...args: A) => R) => {
  if (wait <= 0) return fn

  let lastCall = -Infinity
  let lastResult: R

  return (...args: A) => {
    const now = Date.now()

    if (now - lastCall < wait)
      return opts?.throttledResult ? opts.throttledResult(...args) : lastResult

    lastResult = fn(...args)
    lastCall = now

    return lastResult
  }
}

export function compose<A extends unknown[], R>(fn0: (...args: A) => R): (...arg: A) => R
export function compose<A extends unknown[], R, R0>(
  fn1: (arg: R0) => R,
  fn0: (...args: A) => R0,
): (...arg: A) => R
export function compose<A extends unknown[], R, R0, R1>(
  fn2: (arg: R1) => R,
  fn1: (arg: R0) => R1,
  fn0: (...args: A) => R0,
): (...arg: A) => R
export function compose<A extends unknown[], R, R0, R1, R2>(
  fn3: (arg: R2) => R,
  fn2: (arg: R1) => R2,
  fn1: (arg: R0) => R1,
  fn0: (...args: A) => R0,
): (...arg: A) => R
export function compose(...fns: Function[]): Function {
  const [first, ...rest] = fns.reverse()

  return (...args: any[]) => rest.reduce((acc, fn) => fn(acc), first(...args))
}

// TODO: Identical implementation of this function is used by companion app. Consider moving it to a shared module.
export const once = <A extends unknown[], R>(fn: (...args: A) => R): ((...args: A) => R) => {
  let called = false
  let result: R

  return (...args: A) => {
    if (called) return result

    called = true
    return (result = fn(...args))
  }
}

export const retryAsync = async <R>(
  fn: () => Promise<R> | R,
  nTimes: number,
  {
    onRetry,
    shouldThrowEarly,
  }: {
    onRetry?: (error: Error) => void | Promise<void>
    shouldThrowEarly?: (error: Error) => boolean
  } = {},
): Promise<R> => {
  try {
    return await fn()
  } catch (e) {
    if (nTimes <= 0 || shouldThrowEarly?.(e as Error)) throw e

    await onRetry?.(e as Error)

    return await retryAsync(fn, nTimes - 1)
  }
}
